/* global G */
import { pipe } from 'lib/util'
import { withDescriptor, withObjectFreeze } from 'trait/with'
import { withGetterSetterFn } from '@gaia/trait/with/GetterSetter'
import { hasNamespace } from 'trait/has'
import { usesGetterSetter, usesNamespace } from 'trait/uses'
import api from '@platform/adapter/notification/api'

const descriptor = 'adapter:Notification'

const defaults = {
  init: G.INIT,
  register: G.ACTIVATE,
}

/**
 * Attaches all api methods whose name can be found within {@link defaults} to the event handler by
 * assigning them an event type in the form:
 *
 * @example
 * const groupName = 'default'
 * eventBus.type(G.NOTIFICATION, G.CREATE, groupName) === 'gaia:notification:create:default'
 *
 * @param {Gaia.Web.Application} obj  the Platform Web Application
 * @returns {(function(*): void)|*}
 */
const withEventHandlers = obj => (adapter) => {
  /**
   * @type {Gaia.Adapter.EventBus}
   */
  const eventBus = obj[G.EVENTS]

  Object.keys(defaults).reduce(
    (acc, key) => eventBus.add(
      eventBus.type(G.NOTIFICATION, acc[key]),
      adapter[G.API][key],
    ) || acc,
    defaults,
  )

  return adapter
}

/**
 * Native Web Platform Notification Adapter
 *
 * @memberOf Gaia.Adapter#
 * @typedef Adapter.Notification
 *
 * @property {String} _name             module descriptor. see {@link withDescriptor}
 * @property {Object} G.API             adapter api
 * @property {Adapter.Message.API} API  adapter api revised
 *
 * @param {Gaia.Web.Application} obj    web application
 * @returns {Gaia.Adapter.Notification}  composition - Native Platform Web Adapter
 */
const adapterFn = obj => pipe(
  withDescriptor(descriptor),
  withGetterSetterFn(G.GROUP),
  usesGetterSetter(G.GROUP, {}),
  hasNamespace(G.API),
  usesNamespace(G.API, api(obj)),
  withEventHandlers(obj),
  withObjectFreeze,
)({})

export default adapterFn
